import React, { useState, useRef } from 'react';
import { FaPlus } from 'react-icons/fa';
import { toast } from 'react-toastify';
import CrmTaskService, { CrmTask } from '../services/CrmTaskService';

interface QuickTaskInputProps {
  partnerId?: number | null;
  partnerName?: string | null;
  projectId?: number | null;
  offerId?: number | null;
  invoiceId?: number | null;
  placeholder?: string;
  onTaskCreated: (task: CrmTask) => void;
}

export default function QuickTaskInput({
  partnerId,
  partnerName,
  projectId,
  offerId,
  invoiceId,
  placeholder = 'Add task...',
  onTaskCreated,
}: QuickTaskInputProps): React.JSX.Element {
  const [title, setTitle] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const newTask = await CrmTaskService.create({
        title: trimmedTitle,
        description: '',
        partner: partnerId ?? null,
        project: projectId ?? null,
        offer: offerId ?? null,
        invoice: invoiceId ?? null,
        due_date: CrmTaskService.getDefaultDueDate(),
        priority: 'NORMAL',
      });

      setTitle('');
      onTaskCreated(newTask);
      toast.success('Task created');
    } catch (err) {
      console.error('Failed to create task:', err);
      toast.error('Failed to create task');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      setTitle('');
      inputRef.current?.blur();
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 20px',
        borderBottom: '1px solid #e0e3ef',
        background: isFocused ? '#fafbfc' : 'transparent',
        transition: 'background 0.15s',
      }}
    >
      <div
        style={{
          width: '22px',
          height: '22px',
          borderRadius: '50%',
          border: '2px dashed #d1d5db',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: '#9ca3af',
        }}
      >
        <FaPlus size={10} />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        disabled={isSubmitting}
        style={{
          flex: 1,
          border: 'none',
          outline: 'none',
          background: 'transparent',
          fontSize: '14px',
          color: '#021a4a',
          padding: 0,
        }}
      />
      {title.trim() && (
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          style={{
            padding: '4px 10px',
            borderRadius: '4px',
            border: 'none',
            background: '#7c3aed',
            color: 'white',
            fontSize: '12px',
            fontWeight: 600,
            cursor: isSubmitting ? 'wait' : 'pointer',
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting ? '...' : 'Add'}
        </button>
      )}
    </div>
  );
}
